import { useMemo, useState } from 'react'
import { Archive, Zap } from 'lucide-react'
import { Shell, useShellMeta } from '../../../admin/Shell.js'
import { usePermissions } from '../../../admin/primitives.js'
import { useConfirm } from '../../../shell/confirm.js'
import { Link } from '../../../router.js'
import { fmtDateHeure, fmtHeure, lireSupervision, type Lecture } from '../../api.js'
import {
  BandeauLecture, LIBELLE_ETAT, LIBELLE_GRAVITE, Pastille, TON_ETAT, TON_GRAVITE, renommerSalles, useNomsSalles, useVueSonde, type Ton,
} from '../commun.js'
import { Fait, Quand, Retour, Section, Squelette, Vide, donneeDe, LIBELLE_STATUT } from './composants.js'
import { Courbe } from './Courbe.js'
import { CONFIRMER_NUIT, FormulaireAnnotation, RetourGeste, ZoneTexte, envoyer, sourdineIncident, type Sourdine } from './Formulaires.js'
import {
  ID_INCIDENT, LIBELLE_ETAT_ALERTE, causeEnUneLigne, LIBELLE_ROLE, actionsIncident, cheminAppareil, cheminSalle, courbesBoiteNoire,
  estDocBoiteNoire, estDocIncident, fmtDuree, fmtValeur, libelleType, tonEtatObserve, tonStatutIncident,
  type BoiteNoire, type DocBoiteNoire, type DocIncident, type DonneesBoiteNoire,
} from './modele.js'
import { useMenuSonde } from '../menu-sonde.js'
import { useMenuSalle } from '../menu-salle.js'
import { BoutonActualiser } from '../actualiser.js'

/**
 * FICHE INCIDENT — /infrastructure/incidents/:id (21/10/2026).
 *
 * Lit `incidents/:id` (et `/black-box` sur demande quand la boîte noire est
 * trop grosse pour venir avec la fiche). Une cause, une ligne. Puis les
 * appareils touchés, la chronologie, la boîte noire (±15 min de l'appareil
 * et de sa chaîne d'amont), les notes. Gestes (droit `sonde.incident.ack`) :
 * Acquitter, Résoudre (confirmé), Ajouter une note — seulement quand l'état
 * de l'incident les permet côté Sonde.
 */

const LIBELLE_CLOTURE: Record<string, string> = { manual: 'à la main', stable: 'rétabli et stable', auto: 'automatiquement' }

function EnTete({ d, nomZone }: { d: DocIncident; nomZone: (c: string | null | undefined) => string | null }) {
  const i = d.incident
  const ton = tonStatutIncident(i.status, i.severity)
  const clos = i.status === 'resolved'
  const menuSalle = useMenuSalle()
  return (
    <header className={`sv-fiche-hero sv-fiche-hero--${ton}`}>
      <div className="sv-fiche-hero__haut">
        <div className="sv-fiche-hero__id">
          <p className="sv-fiche-hero__sur">
            Incident{i.zone_code ? <> · <Link href={cheminSalle(i.zone_code)} {...menuSalle.salle({ code: i.zone_code, label: nomZone(i.zone_code) })}>{nomZone(i.zone_code)}</Link></> : null}
          </p>
          <h1 className="lc-title sv-fiche-hero__titre">{renommerSalles(i.title)}</h1>
          <div className="sv-fiche-hero__pastilles">
            <Pastille ton={TON_GRAVITE[i.severity] ?? 'gris'}>{LIBELLE_GRAVITE[i.severity] ?? i.severity}</Pastille>
            <Pastille ton={ton}>{i.status_label || LIBELLE_STATUT[i.status] || i.status}</Pastille>
            <Pastille ton={i.notified ? 'ok' : 'gris'}>{i.notified ? 'Alerte publiée' : 'Aucune alerte publiée'}</Pastille>
            {i.snoozed_until ? <Pastille ton="maint">En sourdine jusqu’à {fmtHeure(i.snoozed_until)}</Pastille> : null}
          </div>
        </div>
      </div>
      <p className="sv-conseq sv-fiche-hero__phrase">
        <span className="sv-fiche-hero__cause">Cause racine</span>{' '}
        {i.root ? <Link href={cheminAppareil(i.root.uid)}>{causeEnUneLigne(i)}</Link> : causeEnUneLigne(i)}
      </p>
      <dl className="sv-fiche-faits">
        <Fait libelle="Ouvert" sous={fmtDateHeure(i.opened_at)}><Quand iso={i.opened_at} /></Fait>
        <Fait libelle={clos ? 'A duré' : 'En cours depuis'}>{fmtDuree(i.duration_s)}</Fait>
        <Fait libelle="Appareils touchés">{i.affected_count}</Fait>
        <Fait libelle="Pris en charge" sous={i.acked_at ? fmtDateHeure(i.acked_at) : null}>{i.acked_by ?? (i.acked_at ? '—' : 'Personne encore')}</Fait>
        {clos ? (
          <Fait libelle="Résolu" sous={i.closed_reason ? LIBELLE_CLOTURE[i.closed_reason] ?? i.closed_reason : null}>
            {i.resolved_by && i.resolved_by !== 'sonde' ? i.resolved_by : 'Sonde'} · <Quand iso={i.resolved_at} />
          </Fait>
        ) : null}
      </dl>
    </header>
  )
}

/* ─────────────── Gestes ─────────────── */

function Gestes({ d, onFait }: { d: DocIncident; onFait: () => void }) {
  const { permissions } = usePermissions()
  const confirm = useConfirm()
  const peutAgir = permissions.includes('sonde.incident.ack')
  const peutAnnoter = permissions.includes('sonde.annotate')
  const a = actionsIncident(d.incident, peutAgir)
  const [raison, setRaison] = useState('')
  const [note, setNote] = useState('')
  const [enCours, setEnCours] = useState<'ack' | 'resolve' | 'note' | 'sourdine' | null>(null)
  const [retour, setRetour] = useState<{ ok: boolean; texte: string } | null>(null)
  const id = d.incident.id

  if (!a.acquitter && !a.resoudre && !a.noter && !peutAnnoter) return null

  const geste = async (quoi: 'ack' | 'resolve' | 'note', corps: Record<string, unknown>, ok: string, rien: string) => {
    setEnCours(quoi); setRetour(null)
    const err = await envoyer(`incidents/${encodeURIComponent(id)}/${quoi}`, corps, rien)
    setEnCours(null)
    if (err) { setRetour({ ok: false, texte: err }); return false }
    setRetour({ ok: true, texte: ok })
    onFait()
    return true
  }

  const sourdine = async (jusqua: Sourdine | null) => {
    if (jusqua === 'night' && !(await confirm({ ...CONFIRMER_NUIT, details: [{ label: 'Incident', value: d.incident.title }] }))) return
    setEnCours('sourdine'); setRetour(null)
    const err = await sourdineIncident(id, jusqua)
    setEnCours(null)
    if (err) { setRetour({ ok: false, texte: err }); return }
    setRetour({ ok: true, texte: jusqua === null ? 'Sourdine levée : les notifications reprennent.' : jusqua === '30m' ? 'En sourdine pour 30 min : Sonde vous le rappellera s’il est toujours ouvert.' : 'Coupé pour la nuit : Sonde vous le rappellera à 8 h s’il est toujours ouvert.' })
    onFait()
  }

  const resoudre = async () => {
    if (!raison.trim()) return
    const oui = await confirm({
      title: 'Résoudre cet incident à la main ?',
      message: 'L’incident est clos et toutes ses alertes sont terminées. Votre raison est gardée dans sa chronologie.',
      details: [{ label: 'Incident', value: d.incident.title }, { label: 'Raison', value: raison.trim().slice(0, 140) }],
      variant: 'sensitive',
      confirmLabel: 'Résoudre',
    })
    if (!oui) return
    if (await geste('resolve', { reason: raison.trim() }, 'Incident résolu.', 'l’incident reste ouvert')) setRaison('')
  }

  return (
    <Section titre="Gestes" id="sv-inc-gestes">
      {a.acquitter ? (
        <div className="sv-fiche-form">
          <p className="sv-fiche-texte">Acquitter dit à l’équipe que quelqu’un s’en occupe : l’incident et ses alertes passent « pris en charge ».</p>
          <div className="sv-fiche-form__actions">
            <button type="button" className="lc-btn lc-btn--primary" disabled={enCours !== null}
                    onClick={() => { void geste('ack', {}, 'Incident acquitté : l’équipe sait que vous vous en occupez.', 'rien n’a été acquitté') }}>
              {enCours === 'ack' ? 'Acquittement…' : 'Acquitter'}
            </button>
          </div>
        </div>
      ) : null}
      {a.sourdine ? (
        <div className="sv-fiche-form">
          <p className="sv-fiche-texte">
            {d.incident.snoozed_until
              ? `En sourdine jusqu’à ${fmtHeure(d.incident.snoozed_until)}${d.incident.snoozed_by ? `, posée par ${d.incident.snoozed_by}` : ''} : aucune notification pour cet incident. S’il est toujours ouvert à l’échéance, Sonde le rappelle.`
              : 'La sourdine coupe les notifications de cet incident pour un temps. S’il est toujours ouvert à l’échéance, Sonde le rappelle.'}
          </p>
          <div className="sv-fiche-form__actions">
            <button type="button" className="lc-btn" disabled={enCours !== null} onClick={() => { void sourdine('30m') }}>Me rappeler dans 30 min</button>
            <button type="button" className="lc-btn" disabled={enCours !== null} onClick={() => { void sourdine('night') }}>Couper pour la nuit</button>
            {a.lever ? <button type="button" className="lc-btn lc-btn--ghost" disabled={enCours !== null} onClick={() => { void sourdine(null) }}>Lever la sourdine</button> : null}
          </div>
        </div>
      ) : null}
      {a.noter ? (
        <div className="sv-fiche-form">
          <ZoneTexte libelle="Ajouter une note" valeur={note} onChange={setNote} max={4000} placeholder="Ce que vous avez vu ou fait." />
          <div className="sv-fiche-form__actions">
            <button type="button" className="lc-btn" disabled={enCours !== null || !note.trim()}
                    onClick={() => { void geste('note', { body: note.trim() }, 'Note ajoutée.', 'la note n’a pas été ajoutée').then((ok) => { if (ok) setNote('') }) }}>
              {enCours === 'note' ? 'Envoi…' : 'Ajouter la note'}
            </button>
          </div>
        </div>
      ) : null}
      {a.resoudre ? (
        <div className="sv-fiche-form">
          <ZoneTexte libelle="Résoudre à la main" valeur={raison} onChange={setRaison} max={2000} placeholder="Pourquoi le clore : ex. faux positif, câble remplacé." />
          <div className="sv-fiche-form__actions">
            <button type="button" className="lc-btn" disabled={enCours !== null || !raison.trim()} onClick={() => { void resoudre() }}>
              {enCours === 'resolve' ? 'Résolution…' : 'Résoudre'}
            </button>
          </div>
        </div>
      ) : null}
      {!peutAgir && peutAnnoter ? (
        <FormulaireAnnotation onFait={onFait}
          cible={d.incident.root ? { incident_id: id, device_uid: d.incident.root.uid } : { incident_id: id }} />
      ) : null}
      <RetourGeste r={retour} />
    </Section>
  )
}

/* ─────────────── Appareils touchés, alertes ─────────────── */

function Touches({ d, nomZone }: { d: DocIncident; nomZone: (c: string | null | undefined) => string | null }) {
  const menu = useMenuSonde()
  return (
    <Section titre="Appareils touchés" id="sv-inc-touches" indice={`${d.members.length}`}>
      {d.members.length === 0 ? <Vide><span>Aucun appareil rattaché : l’incident porte sur une salle ou une source.</span></Vide> : (
        <ul className="sv-fiche-liste">
          {d.members.map((m) => (
            <li key={m.uid}>
              <Link className="sv-fiche-app" href={cheminAppareil(m.uid)} {...menu.appareil({ uid: m.uid, nom: m.display_name, zone_code: m.zone_code })}>
                <span className="sv-fiche-app__corps">
                  <span className="sv-fiche-app__nom">{m.display_name}</span>
                  <span className="sv-fiche-app__meta">{LIBELLE_ROLE[m.role] ?? m.role} · {libelleType(m.kind)}{m.zone_code ? ` · ${nomZone(m.zone_code)}` : ''}</span>
                </span>
                <span className="sv-fiche-app__etat">
                  <Pastille ton={tonEtatObserve(m.status, m.observation, TON_ETAT)}>
                    {m.status ? (m.observation && m.observation !== 'FRESH' ? `${LIBELLE_ETAT[m.status] ?? m.status} (donnée périmée)` : LIBELLE_ETAT[m.status] ?? m.status) : 'Inconnu'}
                  </Pastille>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {d.alerts.length ? (
        <>
          <h3 className="sv-fiche-sous-titre">Alertes</h3>
          <ul className="sv-fiche-kvlist">
            {d.alerts.map((a) => (
              <li key={a.id} className={a.shadow ? 'sv-fiche-ombre' : undefined}>
                <Pastille ton={a.state === 'resolved' ? 'ok' : TON_GRAVITE[a.severity] ?? 'gris'}>{LIBELLE_ETAT_ALERTE[a.state] ?? a.state}</Pastille>{' '}
                {renommerSalles(a.title)}
                <span className="sv-fiche-fait__sous">
                  {a.fired_at ? <>déclenchée à <Quand iso={a.fired_at} /></> : <>vue à <Quand iso={a.first_at} /></>}
                  {a.resolved_at ? <> · terminée à <Quand iso={a.resolved_at} /></> : null}
                  {a.shadow ? ' · mode ombre : observée sans prévenir' : ''}
                  {a.rule_code ? <> · <span className="sv-mono">{a.rule_code}</span></> : null}
                </span>
                {a.state !== 'resolved' ? (
                  <span className="sv-fiche-alerte__geste"><BoutonActualiser petit cible={{ kind: 'alert', id: a.id, nom: renommerSalles(a.title) }} /></span>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Section>
  )
}

/* ─────────────── Chronologie et notes ─────────────── */

function tonLigne(sev: string | null, kind: string): Ton {
  if (kind === 'note' || kind === 'annotation') return 'maint'
  return sev ? TON_GRAVITE[sev] ?? 'gris' : 'gris'
}

function Chronologie({ d }: { d: DocIncident }) {
  const [tout, setTout] = useState(false)
  const l = d.timeline
  const visibles = tout ? l : l.slice(0, 40)
  return (
    <Section titre="Chronologie" id="sv-inc-chrono" indice={`${l.length} entrée${l.length > 1 ? 's' : ''}${d.timeline_truncated ? ' (tronquée)' : ''}`}>
      {l.length === 0 ? <Vide><span>Aucune entrée.</span></Vide> : (
        <ol className="sv-fiche-chrono">
          {visibles.map((e, k) => {
            const corps = typeof e.detail['body'] === 'string' ? e.detail['body'] : typeof e.detail['message_fr'] === 'string' ? e.detail['message_fr'] : null
            return (
              <li key={`${e.at}-${k}`} className={`sv-fiche-chrono__e sv-fiche-chrono__e--${tonLigne(e.severity, e.kind)}`}>
                <span className="sv-fiche-chrono__heure sv-mono"><Quand iso={e.at} /></span>
                <span className="sv-fiche-chrono__corps">
                  <span className="sv-fiche-chrono__titre">{renommerSalles(e.title_fr)}</span>
                  {corps ? <span className="sv-fiche-chrono__detail">{corps}</span> : null}
                  {e.uid ? <span className="sv-fiche-chrono__detail"><Link href={cheminAppareil(e.uid)}>{e.uid}</Link></span> : null}
                </span>
              </li>
            )
          })}
        </ol>
      )}
      {l.length > 40 ? <button type="button" className="lc-btn lc-btn--ghost sv-fiche-plus" onClick={() => setTout((t) => !t)}>{tout ? 'Replier' : `Voir les ${l.length - 40} suivantes`}</button> : null}
    </Section>
  )
}

function Notes({ d }: { d: DocIncident }) {
  const items = useMemo(() => [
    ...d.notes.map((n) => ({ id: `n-${n.id}`, at: n.at, qui: n.author.name, type: 'Note', body: n.body })),
    ...d.annotations.map((n) => ({ id: `a-${n.id}`, at: n.at, qui: n.author, type: 'Régie', body: n.body })),
  ].sort((a, b) => a.at.localeCompare(b.at)), [d])
  return (
    <Section titre="Notes" id="sv-inc-notes" indice={`${items.length}`}>
      {items.length === 0 ? <p className="sv-fiche-doux">Aucune note pour l’instant.</p> : (
        <ul className="sv-fiche-notes">
          {items.map((n) => (
            <li key={n.id}>
              <span className="sv-fiche-notes__tete"><strong>{n.type}</strong> · {n.qui} · <Quand iso={n.at} /></span>
              <span>{n.body}</span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

/* ─────────────── Boîte noire ─────────────── */

const fmtOctets = (n: number) => n >= 1_048_576 ? `${(n / 1_048_576).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo` : n >= 1024 ? `${Math.round(n / 1024)} Ko` : `${n} o`

function UneBoite({ b, openedAt }: { b: BoiteNoire; openedAt: string }) {
  const [tout, setTout] = useState(false)
  const data = (b.data && typeof b.data === 'object' && !Array.isArray(b.data) ? b.data : {}) as DonneesBoiteNoire
  const courbes = useMemo(() => courbesBoiteNoire(data), [data])
  const uidDe = useMemo(() => new Map((data.devices ?? []).map((x) => [x.id, x.uid])), [data])
  const debut = Date.parse(b.from)
  const fin = Date.parse(b.to)
  const visibles = tout ? courbes : courbes.slice(0, 12)
  const evenements = data.events ?? []
  const changements = data.state_changes ?? []
  return (
    <article className="sv-fiche-boite">
      <header className="sv-fiche-boite__tete">
        <strong><Quand iso={b.from} /> → <Quand iso={b.to} /></strong>
        <span className="sv-fiche-doux">
          capturée à <Quand iso={b.captured_at} /> · ouverture à <Quand iso={openedAt} /> · {fmtOctets(b.size_bytes)} · gardée jusqu’au {fmtDateHeure(b.expires_at)}
        </span>
      </header>
      <p className="sv-fiche-texte">
        {b.device_uids.length} appareil{b.device_uids.length > 1 ? 's' : ''} :{' '}
        {b.device_uids.map((u, k) => <span key={u}>{k ? ', ' : ''}<Link href={cheminAppareil(u)}>{u}</Link></span>)}
      </p>
      {b.data_omitted ? <p className="sv-fiche-doux">Contenu trop volumineux pour venir avec la fiche : chargez la boîte noire complète.</p> : null}
      {!b.data_omitted && courbes.length === 0 ? <p className="sv-fiche-doux">Aucune mesure qui varie sur la fenêtre capturée.</p> : null}
      {visibles.length ? (
        <div className="sv-fiche-multiples">
          {visibles.map((c) => {
            const der = [...c.points].reverse().find((p) => p[1] !== null)?.[1] ?? null
            const nom = `${c.label}${c.sous ? ` · ${c.sous}` : ''}`
            return (
              <div key={c.cle} className="sv-fiche-multiple">
                <span className="sv-fiche-multiple__nom">{nom}<span>{fmtValeur(der, c.unite)}</span></span>
                <span className="sv-fiche-multiple__qui">{c.uid}</span>
                <Courbe points={c.points} seuilS={c.seuilS} unite={c.unite} agg="gauge" debut={debut} fin={fin} hauteur={64} libelle={`${nom} — ${c.uid}`} />
              </div>
            )
          })}
        </div>
      ) : null}
      {courbes.length > 12 ? <button type="button" className="lc-btn lc-btn--ghost sv-fiche-plus" onClick={() => setTout((t) => !t)}>{tout ? 'Replier' : `Voir les ${courbes.length} courbes`}</button> : null}
      {changements.length || evenements.length ? (
        <ol className="sv-fiche-chrono sv-fiche-chrono--serre">
          {[
            ...changements.map((c) => ({ at: c.at, ton: TON_ETAT[c.to_status] ?? 'gris' as Ton, texte: `${uidDe.get(c.device_id) ?? '—'} : ${LIBELLE_ETAT[c.to_status] ?? c.to_status}` })),
            ...evenements.map((e) => ({
              at: e.at, ton: TON_GRAVITE[e.severity] ?? 'gris' as Ton,
              texte: `${e.device_id ? uidDe.get(e.device_id) ?? '—' : 'Tout le club'} : ${typeof e.payload?.['message_fr'] === 'string' ? renommerSalles(e.payload['message_fr']) : e.kind}`,
            })),
          ].sort((x, y) => x.at.localeCompare(y.at)).slice(0, 60).map((e, k) => (
            <li key={`${e.at}-${k}`} className={`sv-fiche-chrono__e sv-fiche-chrono__e--${e.ton}`}>
              <span className="sv-fiche-chrono__heure sv-mono"><Quand iso={e.at} /></span>
              <span className="sv-fiche-chrono__corps"><span className="sv-fiche-chrono__titre">{e.texte}</span></span>
            </li>
          ))}
        </ol>
      ) : null}
    </article>
  )
}

function BoiteNoireSection({ d }: { d: DocIncident }) {
  const [complete, setComplete] = useState<Lecture<DocBoiteNoire> | null>(null)
  const [enCours, setEnCours] = useState(false)
  const boites = complete?.etat === 'ok' ? complete.data.black_boxes : d.black_boxes
  const manque = boites.some((b) => b.data_omitted)
  const charger = async () => {
    setEnCours(true)
    setComplete(await lireSupervision(`/v/incidents/${encodeURIComponent(d.incident.id)}/black-box`, estDocBoiteNoire))
    setEnCours(false)
  }
  const statut = d.incident.black_box?.status
  return (
    <Section titre="Boîte noire" id="sv-inc-boite" indice="±15 min de l’appareil et de sa chaîne d’amont"
             actions={manque ? <button type="button" className="lc-btn lc-btn--ghost" disabled={enCours} onClick={() => { void charger() }}>
               <Archive size={14} aria-hidden="true" /> {enCours ? 'Chargement…' : 'Charger la boîte noire complète'}
             </button> : undefined}>
      {complete && complete.etat !== 'ok' ? <p className="sv-fiche-doux">{complete.message}</p> : null}
      {boites.length === 0 ? (
        <Vide><span>{statut === 'pending' ? 'Capture en cours : Sonde enregistre la fenêtre autour de l’ouverture.' : 'Aucune boîte noire capturée pour cet incident.'}</span></Vide>
      ) : boites.map((b) => <UneBoite key={b.id} b={b} openedAt={d.incident.opened_at} />)}
    </Section>
  )
}

/* ─────────────── Page ─────────────── */

export function FicheIncidentPage({ id }: { id: string }) {
  const meta = useShellMeta()
  const valide = ID_INCIDENT.test(id)
  const { lecture, precedente, recharger } = useVueSonde(valide ? `incidents/${encodeURIComponent(id.toLowerCase())}` : null, estDocIncident, 15_000)
  const { data: d, perime } = donneeDe(lecture, precedente)
  const nomZone = useNomsSalles()

  return (
    <Shell active="infrastructure" meta={meta}>
      <div className="lc-page lc-page--wide lc-stagger sv-fiche">
        <Retour repli="/infrastructure/incidents" libelle="Incidents" />
        {!valide ? <Vide><strong>Adresse d’incident invalide.</strong><span>Ouvrez l’incident depuis la liste des incidents.</span></Vide> : null}
        <BandeauLecture lecture={lecture} depuis={null} />
        {valide && !d && !lecture ? <Squelette lignes={6} /> : null}
        {d ? (
          <div className={perime ? 'sv-fiche-perime' : undefined}>
            <EnTete d={d} nomZone={nomZone} />
            <div className="sv-fiche-grille">
              <div className="sv-fiche-grille__cote">
                <Gestes d={d} onFait={recharger} />
                <Touches d={d} nomZone={nomZone} />
                <Notes d={d} />
              </div>
              <div className="sv-fiche-grille__principal">
                <Chronologie d={d} />
                <BoiteNoireSection d={d} />
              </div>
            </div>
          </div>
        ) : null}
        <p className="sv-fiche-pied"><Zap size={14} aria-hidden="true" /> Source : Sonde</p>
      </div>
    </Shell>
  )
}

